import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { UploadCloud, X, CheckCircle2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useSite } from "../context/SiteContext";
import { api, track, formatApiErrorDetail } from "../lib/api";

const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 7; h <= 19; h++) {
    for (const m of [0, 30]) {
      if (h === 19 && m === 30) continue;
      const ampm = h < 12 ? "AM" : "PM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      out.push(`${h12}:${m === 0 ? "00" : "30"} ${ampm}`);
    }
  }
  return out;
})();

const MAX_MB = 10;

export const EstimateForm = () => {
  const [searchParams] = useSearchParams();
  const { L, lang, services } = useSite();
  const F = L.form;
  const serviceOptions = services.map((s) => s.title);
  const [selectedServices, setSelectedServices] = useState(
    searchParams.get("service") ? [searchParams.get("service")] : []
  );
  const toggleService = (s) => {
    if (!started.current) { started.current = true; track("estimate_form_started"); }
    setSelectedServices((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", city: "", zip_code: "",
    service: searchParams.get("service") || "", description: "", contact_method: "Phone",
    preferred_date: "", preferred_time: "",
  });
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const started = useRef(false);
  const fileInput = useRef(null);

  const set = (k) => (e) => {
    if (!started.current) { started.current = true; track("estimate_form_started"); }
    setForm({ ...form, [k]: e.target.value });
  };

  const addFiles = (list) => {
    const ok = [];
    for (const f of Array.from(list)) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!["jpg", "jpeg", "png", "heic", "webp"].includes(ext)) { toast.error(`${f.name}: only JPG, PNG, HEIC or WEBP allowed`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name} exceeds ${MAX_MB}MB`); continue; }
      ok.push(f);
    }
    setFiles((prev) => [...prev, ...ok].slice(0, 10));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || selectedServices.length === 0) { toast.error(F.required); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.set("service", selectedServices.join(", "));
      fd.append("source", "website");
      files.forEach((f) => fd.append("photos", f));
      await api.post("/estimate", fd);
      track("estimate_form_submitted", { service: form.service });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="bg-white border border-slate-200 p-10 md:p-16 text-center" data-testid="estimate-success">
        <CheckCircle2 className="w-16 h-16 text-[#005AE0] mx-auto mb-6" />
        <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-[#0B1B3D] mb-4">Thank you!</h3>
        <p className="text-slate-600 text-lg leading-relaxed max-w-md mx-auto">
          Your request has been received. Noyola Construction LLC will contact you soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 p-6 md:p-10 space-y-6" data-testid="estimate-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div><label className="field-label">{F.name} *</label>
          <input className="field" data-testid="estimate-name-input" value={form.name} onChange={set("name")} placeholder={F.namePh} required /></div>
        <div><label className="field-label">{F.phone} *</label>
          <input className="field" type="tel" data-testid="estimate-phone-input" value={form.phone} onChange={set("phone")} placeholder="(256) 555-0100" required /></div>
        <div><label className="field-label">{F.email}</label>
          <input className="field" type="email" data-testid="estimate-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" /></div>
        <div><label className="field-label">{F.address}</label>
          <input className="field" data-testid="estimate-address-input" value={form.address} onChange={set("address")} /></div>
        <div><label className="field-label">{F.city}</label>
          <input className="field" data-testid="estimate-city-input" value={form.city} onChange={set("city")} /></div>
        <div><label className="field-label">{F.zip}</label>
          <input className="field" data-testid="estimate-zip-input" value={form.zip_code} onChange={set("zip_code")} /></div>
        <div className="md:col-span-2"><label className="field-label">{F.service} *</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-slate-200 bg-slate-50 p-4" data-testid="estimate-service-select">
            {[...serviceOptions, "Other"].map((s) => (
              <label key={s} className={`flex items-center gap-3 px-3 py-2.5 border cursor-pointer text-sm font-semibold transition-colors ${
                selectedServices.includes(s) ? "bg-[#005AE0] text-white border-[#005AE0]" : "bg-white text-slate-700 border-slate-200 hover:border-[#005AE0]"}`}
                data-testid={`estimate-service-opt-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <input type="checkbox" className="accent-white" checked={selectedServices.includes(s)} onChange={() => toggleService(s)} />
                {s}
              </label>
            ))}
          </div>
        </div>
        <div><label className="field-label">{F.contactMethod}</label>
          <select className="field" data-testid="estimate-contact-select" value={form.contact_method} onChange={set("contact_method")}>
            {["Phone", "Text Message", "Email"].map((m) => <option key={m} value={m}>{F.methods[m]}</option>)}
          </select></div>
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div><label className="field-label">{F.date}</label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid="estimate-date-btn"
                className="field flex items-center justify-between text-left">
                <span className={date ? "text-[#0A0A0A]" : "text-slate-400"}>
                  {date ? format(date, "EEEE, MMMM d, yyyy", { locale: lang === "es" ? es : undefined }) : F.selectDate}
                </span>
                <CalendarDays className="w-5 h-5 text-[#005AE0] shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-none border-slate-300" align="start" data-testid="estimate-date-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => {
                  if (!started.current) { started.current = true; track("estimate_form_started"); }
                  setDate(d);
                  setForm((f) => ({ ...f, preferred_date: d ? format(d, "yyyy-MM-dd") : "" }));
                  setDateOpen(false);
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div><label className="field-label">{F.time}</label>
          <select className="field" data-testid="estimate-time-select" value={form.preferred_time} onChange={set("preferred_time")}>
            <option value="">{F.selectTime}</option>
            {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        </div>
        <div className="md:col-span-2"><label className="field-label">{F.about}</label>
          <textarea className="field" rows={5} data-testid="estimate-description-input" value={form.description} onChange={set("description")} placeholder={F.aboutPh} /></div>
      </div>

      <div>
        <label className="field-label">{F.upload}</label>
        <button type="button" data-testid="estimate-upload-zone" onClick={() => fileInput.current?.click()}
          className="w-full border-2 border-dashed border-slate-300 hover:border-[#005AE0] transition-colors py-10 flex flex-col items-center gap-3 text-slate-500">
          <UploadCloud className="w-8 h-8 text-[#005AE0]" />
          <span className="text-sm font-semibold">{F.uploadBtn}</span>
        </button>
        <input ref={fileInput} type="file" multiple accept=".jpg,.jpeg,.png,.heic,.webp,image/*" className="hidden"
          data-testid="estimate-file-input" onChange={(e) => addFiles(e.target.files)} />
        {files.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-4">
            {files.map((f, i) => (
              <div key={i} className="relative w-20 h-20 border border-slate-200 bg-slate-100">
                {f.type.startsWith("image/") && <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />}
                <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-[#0B1B3D] text-white rounded-full flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        {F.auth}
      </p>
      <button type="submit" disabled={busy} data-testid="estimate-submit-btn" className="btn-primary w-full md:w-auto">
        {busy ? F.submitting : F.submit}
      </button>
    </form>
  );
};
